import { useAuth } from "@/components/AuthContext";
import {
  FlashcardData,
  FlashcardTooltip,
  type WordActionAnchor,
  WordActionBar,
} from "@/components/flashcard";
import { useFlashcards } from "@/hooks/useFlashcards";
import { useUserPreferences } from "@/hooks/useLocalStorage";
import { createLogger } from "@/lib/logger";
import { resolveTranslationLanguageCode } from "@/lib/translation-language";
import { Room, RoomEvent } from "livekit-client";
import { ChevronDown, ChevronUp } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import languages from "../data/languages.json";
import { useFillInTheGapEngine } from "./fill-in-the-gap/hooks/useFillInTheGapEngine";
import { ExerciseData, FillInTheGapCanvasProps } from "./fill-in-the-gap/types";
import CanvasHeader from "./fill-in-the-gap/ui/CanvasHeader";
import CanvasPracticePanel from "./fill-in-the-gap/ui/CanvasPracticePanel";
import { ExerciseSkeleton } from "./fill-in-the-gap/ui/ExerciseSkeleton";
import ProgressSection from "./fill-in-the-gap/ui/ProgressSection";
import { TranslationLanguageSelect } from "./fill-in-the-gap/ui/TranslationLanguageSelect";
import {
  formatExerciseExport,
  getExerciseInstructionLanguage,
  getLevelDescription,
} from "./fill-in-the-gap/utils";

const logger = createLogger("FillInTheGapCanvas");

// Sound synthesis utility
const AudioContext =
  typeof window !== "undefined" ? window.AudioContext || (window as any).webkitAudioContext : null;
let audioCtx: AudioContext | null = null;

const FillInTheGapCanvas: React.FC<FillInTheGapCanvasProps> = ({
  exerciseContent,
  onClose,
  exerciseOptions,
  selectedLanguage: initialLanguage = "Spanish",
  fromLanguageName,
  translationLanguageCode: controlledTranslationLanguageCode,
  onTranslationLanguageChange,
  selectedLanguageCode: initialLanguageCode = "es-ES",
  selectedLanguageLevel: initialLanguageLevel = "beginner",
  pendingAnswerSubmission,
  onAnswerSubmitted,
  room,
}) => {
  const playSound = useCallback((type: "pop" | "error" | "success") => {
    if (typeof window === "undefined" || !AudioContext) return;

    // Check audio setting
    const isEnabled = localStorage.getItem("chicky_sound_effects_enabled") === "true";
    if (!isEnabled) return;

    if (!audioCtx) {
      audioCtx = new AudioContext();
    }

    if (audioCtx.state === "suspended") audioCtx.resume();

    if (type === "pop") {
      const osc = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      osc.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      osc.type = "square";
      osc.frequency.setValueAtTime(400, audioCtx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(600, audioCtx.currentTime + 0.1);
      gainNode.gain.setValueAtTime(0.1, audioCtx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.1);
      osc.start();
      osc.stop(audioCtx.currentTime + 0.1);
    } else if (type === "error") {
      const osc = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      osc.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      osc.type = "sawtooth";
      osc.frequency.setValueAtTime(150, audioCtx.currentTime);
      osc.frequency.linearRampToValueAtTime(100, audioCtx.currentTime + 0.2);
      gainNode.gain.setValueAtTime(0.2, audioCtx.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.01, audioCtx.currentTime + 0.3);
      osc.start();
      osc.stop(audioCtx.currentTime + 0.3);
    } else if (type === "success") {
      const now = audioCtx.currentTime;
      [440, 554, 659, 880].forEach((freq, i) => {
        const osc = audioCtx!.createOscillator();
        const gainNode = audioCtx!.createGain();
        osc.type = "square";
        osc.frequency.value = freq;
        osc.connect(gainNode);
        gainNode.connect(audioCtx!.destination);
        gainNode.gain.setValueAtTime(0.1, now + i * 0.1);
        gainNode.gain.exponentialRampToValueAtTime(0.001, now + i * 0.1 + 0.2);
        osc.start(now + i * 0.1);
        osc.stop(now + i * 0.1 + 0.2);
      });
    }
  }, []);
  const [isProgressVisible, setIsProgressVisible] = useState(false);
  const [showOptions, setShowOptions] = useState(true);
  const [autoSubmit, setAutoSubmit] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [soundEffectsEnabled, setSoundEffectsEnabled] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("chicky_sound_effects_enabled") === "true";
    }
    return false; // Default off
  });
  const [ttsEnabled, setTtsEnabled] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("chicky_tts_enabled") === "true";
    }
    return false; // Default off
  });
  const [ttsSpeed, setTtsSpeed] = useState<number>(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("chicky_tts_speed");
      if (saved) {
        const val = parseFloat(saved);
        if (!isNaN(val) && val >= 0.5 && val <= 1.5) return val;
      }
    }
    return 1.0;
  });

  const handleTtsSpeedChange = (speed: number) => {
    const clamped = Math.max(0.5, Math.min(1.5, Math.round(speed * 20) / 20));
    setTtsSpeed(clamped);
    if (typeof window !== "undefined") {
      localStorage.setItem("chicky_tts_speed", clamped.toString());
    }
  };
  const [showAudioSettings, setShowAudioSettings] = useState(false);

  // Language and level controls (now mutable)
  const [selectedLanguage, setSelectedLanguage] = useState(initialLanguage);
  const [selectedLanguageCode, setSelectedLanguageCode] = useState(initialLanguageCode);
  const { preferences, setPreferences } = useUserPreferences();
  const translationLanguageCode = resolveTranslationLanguageCode({
    targetLanguageCode: selectedLanguageCode,
    preferredLanguageCode: controlledTranslationLanguageCode ?? preferences.translationLanguageCode,
  });
  const handleTranslationLanguageChange = (code: string) => {
    if (onTranslationLanguageChange) {
      onTranslationLanguageChange(code);
    } else {
      setPreferences((previous) => ({ ...previous, translationLanguageCode: code }));
    }
  };

  // Flashcard state
  const [selectedWordForFlashcard, setSelectedWordForFlashcard] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<WordActionAnchor>({ x: 0, y: 0 });
  const [actionBarWord, setActionBarWord] = useState<string | null>(null);
  const [actionBarPosition, setActionBarPosition] = useState<WordActionAnchor>({
    x: 0,
    y: 0,
  });

  const { user } = useAuth();
  const posthog = usePostHog();
  const currentLevelRef = useRef(0);
  const { isFlashcardSaved, isFlashcardFavorited, saveFlashcard, toggleFavorite } = useFlashcards(
    user?.id
  );

  const handleFlashcardWordClick = (word: string, position: WordActionAnchor) => {
    setActionBarWord(word);
    setActionBarPosition(position);
  };

  const handleCloseActionBar = () => {
    setActionBarWord(null);
  };

  const handleOpenFlashcard = (word: string, position: WordActionAnchor) => {
    setSelectedWordForFlashcard(word);
    setTooltipPosition(position);
    setActionBarWord(null); // Close action bar when opening flashcard
  };

  const handleCloseTooltip = () => {
    setSelectedWordForFlashcard(null);
  };

  const handleSaveFlashcard = async (sentence: string, data: FlashcardData) => {
    if (!selectedLanguage) return;
    await saveFlashcard(sentence, selectedLanguage, exerciseInstructionLanguage, data);
  };

  const handleToggleFavorite = async (sentence: string) => {
    if (!selectedLanguage) return;
    await toggleFavorite(sentence, selectedLanguage);
  };

  // Settings with localStorage persistence
  const [hideTranslations, setHideTranslations] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("chicky_hide_translations") === "true";
    }
    return false;
  });

  const [hideTranscription, setHideTranscription] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("chicky_hide_transcription") === "true";
    }
    return false;
  });

  // Save settings to localStorage
  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("chicky_hide_translations", hideTranslations.toString());
    }
  }, [hideTranslations]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("chicky_hide_transcription", hideTranscription.toString());
    }
  }, [hideTranscription]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("chicky_sound_effects_enabled", soundEffectsEnabled.toString());
    }
  }, [soundEffectsEnabled]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("chicky_tts_enabled", ttsEnabled.toString());
    }
  }, [ttsEnabled]);

  // Function to send exercise data to the agent
  const sendExerciseToAgent = useCallback(
    (exercise: ExerciseData) => {
      // Only send to agent if room is connected and has an active participant
      if (room && exercise && room.localParticipant && room.localParticipant.identity) {
        try {
          // Check if the room connection is actually active
          if (room.state !== "connected") {
            logger.debug("Room not connected, skipping agent communication");
            return;
          }

          const formattedContent = exercise.content.replace(/_+/g, "(blank)");
          const exerciseMessage = {
            type: "exercise_loaded",
            exercise: {
              sentence: formattedContent,
              content: formattedContent,
              translation: exercise.translation,
              language: exercise.language,
              level: exercise.level,
              correctAnswer: exercise.correctAnswer,
              options: exercise.options,
            },
          };

          room.localParticipant.publishData(
            new TextEncoder().encode(JSON.stringify(exerciseMessage)),
            { reliable: true }
          );

          logger.info("📤 Sent exercise data to agent:", exerciseMessage);
        } catch (error) {
          logger.debug(
            "Canvas running in standalone mode, agent communication skipped:",
            error instanceof Error ? error.message : String(error)
          );
        }
      } else {
        logger.debug("Canvas running in standalone mode - no agent communication needed");
      }
    },
    [room]
  );

  // Function to send answer result to the agent
  const sendAnswerResultToAgent = useCallback(
    (result: string, isCorrect: boolean, userAnswer: string, exerciseSentence: string) => {
      // Only send to agent if room is connected and has an active participant
      if (room && room.localParticipant && room.localParticipant.identity) {
        try {
          // Check if the room connection is actually active
          if (room.state !== "connected") {
            logger.debug("Room not connected, skipping agent communication");
            return;
          }

          const resultMessage = {
            type: "answer_result",
            result: result,
            correct: isCorrect,
            userAnswer: userAnswer,
            exerciseSentence: exerciseSentence || "",
          };

          room.localParticipant.publishData(
            new TextEncoder().encode(JSON.stringify(resultMessage)),
            { reliable: true }
          );

          logger.info("📤 Sent answer result to agent:", resultMessage);
        } catch (error) {
          logger.debug(
            "Canvas running in standalone mode, agent communication skipped:",
            error instanceof Error ? error.message : String(error)
          );
        }
      } else {
        logger.debug("Canvas running in standalone mode - no agent communication needed");
      }
    },
    [room]
  );

  const [streak, setStreak] = useState(0);

  const handleAnswerResult = useCallback(
    (result: string, isCorrect: boolean, userAnswer: string, exerciseSentence: string) => {
      setStreak((prev) => (isCorrect ? prev + 1 : 0));
      if (posthog && process.env.NEXT_PUBLIC_POSTHOG_KEY) {
        posthog.capture("fill_in_gap_answer_checked", {
          is_correct: isCorrect,
          language: selectedLanguage,
          language_code: selectedLanguageCode,
          level: currentLevelRef.current,
          auto_submit: autoSubmit,
          answer_length: userAnswer.length,
          exercise_sentence_length: exerciseSentence.length,
        });
      }
      sendAnswerResultToAgent(result, isCorrect, userAnswer, exerciseSentence);
    },
    [autoSubmit, posthog, selectedLanguage, selectedLanguageCode, sendAnswerResultToAgent]
  );

  const {
    exerciseQueue,
    navigation,
    exerciseData,
    isLoading,
    feedback,
    isCorrect,
    showNextButton,
    showExplanation,
    setShowExplanation,
    currentLevel,
    setCurrentLevel,
    levelBeforeCheck,
    showSuccessOverlay,
    setShowSuccessOverlay,
    shakeWidget,
    currentSentence,
    poolWords,
    discardedWordKeys,
    getWordKey,
    typedAnswer,
    handleTypedAnswerChange,
    handleWordClick,
    checkAnswer,
    handleNext,
    handlePrevious,
    handleNavigationNext,
    handleRefresh,
    generateExercise,
    clearAutoAdvance,
    speakSentence,
    isSpeaking,
  } = useFillInTheGapEngine({
    exerciseContent,
    exerciseOptions,
    selectedLanguage,
    selectedLanguageCode,
    initialLanguageLevel,
    fromLanguageName,
    translationLanguageCode,
    ttsEnabled,
    ttsSpeed,
    autoSubmit,
    playSound,
    onExerciseLoaded: sendExerciseToAgent,
    onAnswerResult: handleAnswerResult,
    pendingAnswerSubmission,
    onAnswerSubmitted,
  });

  const exerciseInstructionLanguage = getExerciseInstructionLanguage(exerciseData);

  useEffect(() => {
    currentLevelRef.current = currentLevel;
  }, [currentLevel]);

  // Update language name when language code changes
  useEffect(() => {
    const languageName = Object.entries(languages).find(
      ([, code]) => code === selectedLanguageCode
    )?.[0];
    if (languageName) {
      setSelectedLanguage(languageName);
    }
  }, [selectedLanguageCode]);

  // Handle incoming data messages from the agent
  useEffect(() => {
    logger.debug("FillInTheGapCanvas mounted/updated - checking for updates");
    if (!room) return;

    const handleDataReceived = (data: any, participant: any) => {
      try {
        const message = JSON.parse(new TextDecoder().decode(data));
        logger.info("📥 Canvas received message from agent:", message);

        switch (message.type) {
          case "request_exercise":
            // Agent is requesting an exercise to be loaded
            logger.info("🔄 Agent requested exercise, generating...");
            if (!exerciseData) {
              generateExercise();
            } else {
              // Re-send current exercise data to agent
              sendExerciseToAgent(exerciseData);
            }
            break;

          case "request_new_exercise":
            // Agent is specifically requesting a new exercise
            logger.info("🔄 Agent requested new exercise, simulating 'Next' button click...");
            handleNext();
            break;

          default:
            logger.info("❓ Unknown message type from agent:", message.type);
        }
      } catch (error) {
        logger.error("❌ Error parsing agent message:", error);
      }
    };

    room.on(RoomEvent.DataReceived, handleDataReceived);

    return () => {
      room.off(RoomEvent.DataReceived, handleDataReceived);
    };
  }, [room, exerciseData, generateExercise, sendExerciseToAgent, handleNext]);

  // Export sentences to a .txt file
  const handleExport = useCallback(() => {
    try {
      if (navigation.exerciseHistory.length === 0) {
        logger.info("No exercises to export");
        return;
      }

      const sentences = navigation.exerciseHistory.map(formatExerciseExport);

      const content = `Chicky Tutor Sentences - ${selectedLanguage} (${new Date().toLocaleDateString()})\n\n${sentences.join("\n")}`;
      const blob = new Blob([content], { type: "text/plain;charset=utf-8" });

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `chicky_sentences_${selectedLanguage.toLowerCase()}_${new Date().toISOString().split("T")[0]}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      logger.info(`Exported ${navigation.exerciseHistory.length} sentences`);
    } catch (error) {
      logger.error("Failed to export sentences:", error);
    }
  }, [navigation.exerciseHistory, selectedLanguage]);

  if (!exerciseData) {
    if (isLoading) return <ExerciseSkeleton />;

    return (
      <div className="min-h-[100dvh] bg-[#0F0F14] text-white">
        <CanvasHeader
          selectedLanguage={selectedLanguage}
          currentLevel={currentLevel}
          historyCount={navigation.exerciseHistory.length}
          streak={streak}
          onExport={handleExport}
          onClose={onClose}
        />
        <main className="mx-auto flex w-full max-w-5xl px-4 py-8">
          <section className="w-full rounded-lg border border-red-400/30 bg-[#15161B] p-6 sm:p-8">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-red-300">
              Exercise unavailable
            </p>
            <h1 className="mt-3 text-2xl font-semibold">We could not prepare this exercise.</h1>
            <p className="mt-2 max-w-xl text-sm leading-relaxed text-white/60">
              Your language and difficulty are still selected. Try the request again or close Visual
              Practice.
            </p>
            <div className="mt-4 max-w-sm">
              <TranslationLanguageSelect
                targetLanguageCode={selectedLanguageCode}
                languageCode={translationLanguageCode}
                onLanguageChange={handleTranslationLanguageChange}
              />
            </div>
            <button
              type="button"
              onClick={() => generateExercise()}
              className="mt-6 min-h-12 rounded-lg bg-[#FD954F] px-6 font-semibold text-[#17120E] transition-colors hover:bg-[#FFA968]"
            >
              Try again
            </button>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] overflow-y-auto bg-[#0F0F14] pb-safe text-white">
      <style jsx>{`
        @keyframes shake {
          10%,
          90% {
            transform: translate3d(-4px, 0, 0);
          }
          20%,
          80% {
            transform: translate3d(4px, 0, 0);
          }
          30%,
          50%,
          70% {
            transform: translate3d(-8px, 0, 0);
          }
          40%,
          60% {
            transform: translate3d(8px, 0, 0);
          }
        }

        .widget-shake {
          animation: shake 0.5s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
        }

        @media (prefers-reduced-motion: reduce) {
          .widget-shake {
            animation: none;
          }
        }
      `}</style>

      <CanvasHeader
        selectedLanguage={selectedLanguage}
        currentLevel={currentLevel}
        historyCount={navigation.exerciseHistory.length}
        streak={streak}
        onExport={handleExport}
        onClose={onClose}
      />

      <main className="mx-auto w-full max-w-5xl px-4 pb-8 pt-5 sm:pt-7">
        {/* On mobile, ProgressSection is collapsible. On desktop, it's always visible. */}
        <div className="mb-4 md:hidden">
          <div className="border-b border-white/10 pb-4">
            <button
              type="button"
              onClick={() => setIsProgressVisible(!isProgressVisible)}
              className="flex min-h-11 w-full items-center justify-between rounded-lg px-1 text-sm font-medium text-white"
              aria-expanded={isProgressVisible}
            >
              <span>Practice setup</span>
              {isProgressVisible ? (
                <ChevronUp className="h-5 w-5 text-white/55" aria-hidden="true" />
              ) : (
                <ChevronDown className="h-5 w-5 text-white/55" aria-hidden="true" />
              )}
            </button>
            {isProgressVisible && (
              <div className="mt-3">
                <ProgressSection
                  selectedLanguage={selectedLanguage}
                  selectedLanguageCode={selectedLanguageCode}
                  setSelectedLanguageCode={setSelectedLanguageCode}
                  currentLevel={currentLevel}
                  setCurrentLevel={setCurrentLevel}
                  exerciseQueue={exerciseQueue.exerciseQueue}
                  isGeneratingBatch={exerciseQueue.isGeneratingBatch}
                  getQueueKey={exerciseQueue.getQueueKey}
                  getLevelDescription={getLevelDescription}
                />
              </div>
            )}
          </div>
        </div>
        <div className="hidden md:block">
          <ProgressSection
            selectedLanguage={selectedLanguage}
            selectedLanguageCode={selectedLanguageCode}
            setSelectedLanguageCode={setSelectedLanguageCode}
            currentLevel={currentLevel}
            setCurrentLevel={setCurrentLevel}
            exerciseQueue={exerciseQueue.exerciseQueue}
            isGeneratingBatch={exerciseQueue.isGeneratingBatch}
            getQueueKey={exerciseQueue.getQueueKey}
            getLevelDescription={getLevelDescription}
          />
        </div>

        <CanvasPracticePanel
          className={shakeWidget ? "widget-shake" : ""}
          exerciseData={exerciseData}
          selectedLanguage={selectedLanguage}
          selectedLanguageCode={selectedLanguageCode}
          canGoToPrevious={navigation.canGoToPrevious}
          translationLanguageCode={translationLanguageCode}
          onTranslationLanguageChange={handleTranslationLanguageChange}
          historyIndex={navigation.currentHistoryIndex}
          historyLength={navigation.exerciseHistory.length}
          showSettings={showSettings}
          setShowSettings={setShowSettings}
          showAudioSettings={showAudioSettings}
          setShowAudioSettings={setShowAudioSettings}
          soundEffectsEnabled={soundEffectsEnabled}
          setSoundEffectsEnabled={setSoundEffectsEnabled}
          ttsEnabled={ttsEnabled}
          setTtsEnabled={setTtsEnabled}
          ttsSpeed={ttsSpeed}
          onTtsSpeedChange={handleTtsSpeedChange}
          hideTranslations={hideTranslations}
          setHideTranslations={setHideTranslations}
          hideTranscription={hideTranscription}
          setHideTranscription={setHideTranscription}
          showOptions={showOptions}
          setShowOptions={setShowOptions}
          autoSubmit={autoSubmit}
          setAutoSubmit={setAutoSubmit}
          typedAnswer={typedAnswer}
          currentSentence={currentSentence}
          poolWords={poolWords}
          discardedWordKeys={discardedWordKeys}
          getWordKey={getWordKey}
          showNextButton={showNextButton}
          showSuccessFeedback={showSuccessOverlay}
          levelIncrease={Math.max(0, currentLevel - levelBeforeCheck)}
          streak={streak}
          isLoading={isLoading}
          feedback={feedback}
          isCorrect={isCorrect}
          showExplanation={showExplanation}
          setShowExplanation={setShowExplanation}
          initialLanguageLevel={initialLanguageLevel}
          onPrevious={handlePrevious}
          onNextNavigation={handleNavigationNext}
          onNextExercise={handleNext}
          onReviewSuccess={() => {
            setShowSuccessOverlay(false);
            clearAutoAdvance();
            setShowExplanation(true);
          }}
          onRefresh={handleRefresh}
          onCheckAnswer={(answer, meta) => checkAnswer(answer, meta)}
          onTypedAnswerChange={handleTypedAnswerChange}
          onWordClick={handleWordClick}
          onFlashcardWordClick={handleFlashcardWordClick}
          onSpeakSentence={speakSentence}
          isSpeaking={isSpeaking}
        />

        <div className="h-6 md:h-0" />
      </main>
      {/* Word Action Bar */}
      {actionBarWord && (
        <WordActionBar
          word={actionBarWord}
          position={actionBarPosition}
          onOpenFlashcard={(word) => handleOpenFlashcard(word, actionBarPosition)}
          onClose={handleCloseActionBar}
        />
      )}

      {/* Flashcard Tooltip */}
      {selectedWordForFlashcard && (
        <FlashcardTooltip
          key={`${selectedLanguageCode}:${exerciseInstructionLanguage}:${selectedWordForFlashcard}`}
          sentence={selectedWordForFlashcard}
          position={tooltipPosition}
          onClose={handleCloseTooltip}
          targetLanguage={selectedLanguage}
          instructionLanguage={exerciseInstructionLanguage}
          userId={user?.id}
          onSave={handleSaveFlashcard}
          onFavorite={handleToggleFavorite}
          isFavorited={isFlashcardFavorited(selectedWordForFlashcard, selectedLanguage)}
          isSaved={isFlashcardSaved(selectedWordForFlashcard, selectedLanguage)}
          getSavedState={(text) => ({
            isSaved: isFlashcardSaved(text, selectedLanguage),
            isFavorited: isFlashcardFavorited(text, selectedLanguage),
          })}
        />
      )}
    </div>
  );
};

export default FillInTheGapCanvas;
